import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import MetricCounter from "@/components/shared/MetricCounter";
import { metricsList } from "@/config/metricsConfig";
import { getCaptecPerformanceProfile } from "./scrollPerformance";
import { createOneTimeViewportAnimation } from "./viewportAnimation";

const HAND_CARD_IMAGE = "/assets/hand-card/captec-hand-card.webp";

const HandCardShowcase = ({ enabled = true }) => {
  const sectionRef = useRef(null);
  const [countersActive, setCountersActive] = useState(false);

  const selectedMetrics = useMemo(() => {
    if (!Array.isArray(metricsList)) return [];
    return metricsList.slice(0, 3);
  }, []);

  useLayoutEffect(() => {
    if (!enabled) return undefined;

    const section = sectionRef.current;
    if (!section) return undefined;

    const profile = getCaptecPerformanceProfile();
    const { lowResource, reducedMotion, fastScrollVelocity } = profile;

    let coordinator = null;
    let timeline = null;

    const ctx = gsap.context(() => {
      const copy = section.querySelector(".hand-editorial-copy");
      const imageMotion = section.querySelector(".hand-editorial-image-motion");
      const metrics = section.querySelector(".hand-editorial-metrics");

      const finalState = () => {
        setCountersActive(true);
        gsap.set([copy, imageMotion, metrics], {
          autoAlpha: 1,
          x: 0,
          y: 0,
          scale: 1,
          rotate: 0,
        });
        section.classList.add("is-hand-animation-complete");
        section.classList.remove("is-home-reveal-active");
      };

      if (reducedMotion) {
        finalState();
        return;
      }

      gsap.set(copy, { autoAlpha: 0, y: lowResource ? 10 : 20 });
      gsap.set(imageMotion, {
        autoAlpha: 0,
        x: lowResource ? 24 : 54,
        y: lowResource ? 6 : 14,
        scale: lowResource ? 0.995 : 1.02,
        rotate: lowResource ? 0 : -0.65,
        transformOrigin: "58% 48%",
      });
      gsap.set(metrics, { autoAlpha: 0, y: lowResource ? 8 : 16 });

      timeline = gsap.timeline({ paused: true });
      timeline
        .to(copy, {
          autoAlpha: 1,
          y: 0,
          duration: lowResource ? 0.52 : 0.82,
          ease: "power2.out",
        })
        .to(
          imageMotion,
          {
            autoAlpha: 1,
            x: 0,
            y: 0,
            scale: 1,
            rotate: 0,
            duration: lowResource ? 0.7 : 1.24,
            ease: "power3.out",
          },
          "-=0.42"
        )
        .to(
          metrics,
          {
            autoAlpha: 1,
            y: 0,
            duration: lowResource ? 0.42 : 0.7,
            ease: "power2.out",
            onStart: () => setCountersActive(true),
          },
          "-=0.24"
        );

      coordinator = createOneTimeViewportAnimation({
        target: section,
        preload: -Math.round(window.innerHeight * (lowResource ? 0.08 : 0.18)),
        fastVelocity: fastScrollVelocity * 2.4,
        play: (done) => {
          section.classList.add("is-home-reveal-active");
          timeline.eventCallback("onComplete", () => {
            finalState();
            done();
          });
          timeline.play(0);
        },
        finish: finalState,
      });

    }, section);

    return () => {
      coordinator?.destroy();
      timeline?.kill();
      ctx.revert();
      section.classList.remove(
        "is-hand-animation-complete",
        "is-home-reveal-active"
      );
    };
  }, [enabled]);

  return (
    <section
      ref={sectionRef}
      className="hand-card-showcase-section relative overflow-hidden bg-black text-white"
      data-testid="hand-card-showcase"
    >
      <div className="absolute inset-0 pointer-events-none bg-[var(--surface-deep)]" aria-hidden="true" />


      <div className="hand-editorial-shell relative mx-auto max-w-7xl container-px min-h-[100vh] py-20 lg:py-24 flex flex-col">
        <div className="hand-editorial-copy relative z-20 max-w-[420px] pt-4 lg:pt-0">
          <div className="hand-editorial-kicker mb-4 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-[hsl(var(--cyan))]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--cyan))]">
              Emisión + procesamiento
            </span>
          </div>

          <h2 className="hand-editorial-title font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[0.94] tracking-[-0.055em] text-white">
            Una tarjeta es solo
            <br />
            el comienzo.
          </h2>

          <p className="hand-editorial-text mt-5 text-sm md:text-base leading-7 text-white/62">
            CAPTEC la implementa, certifica, procesa y digitaliza. Integramos emisión física y virtual, personalización, tokenización, billeteras digitales, seguridad y operación 24/7 bajo un modelo llave en mano.
          </p>
        </div>

        <div className="hand-editorial-visual relative flex-1 min-h-[420px] lg:min-h-0">
          <div className="hand-editorial-image-wrap">
            <div className="hand-editorial-image-motion">
              <img
                src={HAND_CARD_IMAGE}
                alt="Mano sosteniendo tarjeta Captec"
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="hand-editorial-image select-none pointer-events-none drop-shadow-[0_42px_92px_rgba(0,0,0,0.72)]"
              />
            </div>
          </div>
        </div>

        {selectedMetrics.length > 0 ? (
          <div className="hand-editorial-metrics relative z-20 mt-4 grid grid-cols-1 sm:grid-cols-3 gap-7 lg:absolute lg:left-0 lg:bottom-20 lg:mt-0 lg:w-full lg:max-w-3xl lg:gap-12">
            {selectedMetrics.map((metric) => (
              <div key={metric.id} className="hand-editorial-metric">
                <p className="text-[11px] leading-none text-white/55">Hasta</p>

                <p className="mt-1 font-display text-3xl lg:text-4xl font-semibold leading-none text-[hsl(var(--cyan))]">
                  <MetricCounter
                    value={metric.value}
                    suffix={metric.suffix}
                    active={countersActive}
                    duration={1900}
                  />
                </p>

                <p className="mt-2 max-w-[160px] text-[11px] leading-4 text-white/55">
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default HandCardShowcase;
